import { GridTypes, DifferenceTypeNames } from 'types';
import { useSelector, useDispatch } from 'react-redux';
import { getFocusedDifference, getDifferencePanelOpen, inspection } from 'store';
import { isPrepUrl } from 'utils/location';
import BarcodeLayout from './BarcodeLayout';
import BrailleLayout from './BrailleLayout';
import ColorLayout from './ColorLayout';
import DifferenceGridLayout from './DifferenceGridLayout';
import { FLOATING_PANEL_Z_INDEX } from './useFloatingPanel';
import { Popper } from '@mui/material';
import { useEffect, useRef } from 'react';

type DifferenceExpansionPanelProps = {
  anchorEl: React.RefObject<HTMLElement>;
};

const DifferenceExpansionPanel: React.FC<DifferenceExpansionPanelProps> = ({ anchorEl }) => {
  const dispatch = useDispatch();
  const currentDifference = useSelector(getFocusedDifference);
  const isPanelOpen = useSelector(getDifferencePanelOpen);
  const previousDifferenceId = useRef<string | null>(null);

  const layoutComponents: Record<string, React.FC<{ id: string }>> = {
    [DifferenceTypeNames.Barcode]: BarcodeLayout,
    [DifferenceTypeNames.Braille]: BrailleLayout,
    [DifferenceTypeNames.Color]: ColorLayout,
  };

  const gridTypesArray = Object.values(GridTypes) as string[];
  const isGridType = currentDifference ? gridTypesArray.includes(currentDifference.type) : false;
  const isDecodedLayoutType =
    currentDifference &&
    (currentDifference.type === DifferenceTypeNames.Barcode || currentDifference.type === DifferenceTypeNames.Braille);
  const isColorType = currentDifference?.type === DifferenceTypeNames.Color;
  const isPopperType = isGridType;
  const LayoutComponent =
    currentDifference && (isGridType ? DifferenceGridLayout : layoutComponents[currentDifference.type]);

  // Auto-open panel when selecting a panel difference (Barcode, Braille, or Color)
  useEffect(() => {
    if (currentDifference?.id && currentDifference.id !== previousDifferenceId.current) {
      previousDifferenceId.current = currentDifference.id;
      if (!isGridType && !isPanelOpen) {
        dispatch(inspection.actions.setDifferencePanelOpen(true));
      }
    } else if (!currentDifference?.id) {
      previousDifferenceId.current = null;
    }
  }, [currentDifference?.id, isPanelOpen, isGridType, dispatch]);

  const shouldShowPopup = !isPrepUrl() && !!anchorEl.current && !!currentDifference;

  // For Grid types, use Popper
  if (shouldShowPopup && isPopperType && LayoutComponent) {
    const componentId = `${currentDifference?.id}-${currentDifference?.type}`;

    return (
      <Popper
        key="grid"
        open
        placement="bottom-start"
        anchorEl={anchorEl.current}
        style={{ zIndex: FLOATING_PANEL_Z_INDEX }}
        modifiers={[
          { name: 'offset', options: { offset: [40, 0] } },
          { name: 'preventOverflow', options: { padding: 8, boundary: 'viewport' } },
          { name: 'flip', enabled: false },
        ]}
      >
        <LayoutComponent id={componentId} />
      </Popper>
    );
  }

  // For DecodedLayout types (Barcode/Braille), render directly positioned relative to documents-panel
  if (shouldShowPopup && isDecodedLayoutType && LayoutComponent) {
    return <LayoutComponent id={`${currentDifference?.id}-${currentDifference?.type}`} />;
  }

  // For Color types, render directly (ColorLayout handles its own floating panel positioning)
  if (shouldShowPopup && isColorType) {
    return <ColorLayout id={`${currentDifference?.id}-${currentDifference?.type}`} />;
  }

  return null;
};

export default DifferenceExpansionPanel;
